"use client";

import { c as _c } from "react/compiler-runtime";
import { useQueries } from "@tanstack/react-query";
import type { FormTable } from "@/types/forms/form";
import { formQueryOptions } from "@/api/forms/form-query";
const CHECK_FAILED = "Decoda could not check these forms. Reload the page and try again.";

/** "A", "A and B", "A, B, and C". */
function nameList(names: string[]): string {
  if (names.length < 3) return names.join(" and ");
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
}
function blockedReasonFor(linked: string[], unlinked: string[]): string | null {
  const sentences: string[] = [];
  if (linked.length === 1) {
    sentences.push(`${linked[0]} is a membership form. Send it from the patient's membership instead.`);
  } else if (linked.length > 1) {
    sentences.push(`${nameList(linked)} are membership forms. Send them from the patient's membership instead.`);
  }
  if (unlinked.length === 1) {
    sentences.push(`${unlinked[0]} fills in membership details, but no plan uses it yet. Set it as a plan's Required Form.`);
  } else if (unlinked.length > 1) {
    sentences.push(`${nameList(unlinked)} fill in membership details, but no plan uses them yet. Set each as a plan's Required Form.`);
  }
  return sentences.length > 0 ? sentences.join(" ") : null;
}
export interface MembershipFormGuard {
  /** No probe has answered yet, or a cached answer is being re-checked. */
  isPending: boolean;
  /** Selected forms that must not travel: they render a plan's details. */
  blockedFormIds: string[];
  /** Names them, or names a probe that never answered. */
  blockedReason: string | null;
  /** A probe failed — nothing can be described, so nothing may send. */
  checkFailed: boolean;
}
const NOTHING_BLOCKED: MembershipFormGuard = {
  isPending: false,
  blockedFormIds: [],
  blockedReason: null,
  checkFailed: false
};

/** Whether these forms may be sent without naming a membership plan.
 *
 * A form whose text names `{membership.*}` renders one plan's price and terms, and
 * `Membership.form_id` is a plain FK — so several plans can name the same agreement and
 * a send carrying only a form id cannot identify a price. Such a form is offered from
 * the patient's membership, where the plan is known, and refused everywhere else.
 *
 * `isPending` and `checkFailed` are folded into the return rather than left for the
 * caller, because an unanswered probe is indistinguishable from "ordinary form" — the
 * one answer that lets a membership form travel. Picking a form and clicking Send is a
 * single motion, so a gate that reads only the resolved answers sends unasked in the few
 * hundred milliseconds before they arrive.
 */
export function useMembershipFormGuard(forms, enabled) {
  const $ = _c(11);
  let t0;
  if ($[0] !== enabled || $[1] !== forms) {
    let t1;
    if ($[3] !== enabled) {
      t1 = form => ({
        ...formQueryOptions.membershipContext(form.id),
        enabled,
        retry: false
      });
      $[3] = enabled;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    t0 = forms.map(t1);
    $[0] = enabled;
    $[1] = forms;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[5] !== enabled || $[6] !== forms) {
    t1 = results => {
      if (!enabled) {
        return NOTHING_BLOCKED;
      }
      if (results.some(_temp)) {
        return {
          isPending: false,
          blockedFormIds: [],
          blockedReason: CHECK_FAILED,
          checkFailed: true
        };
      }
      const blockedFormIds = [];
      const linked = [];
      const unlinked = [];
      forms.forEach((form_0, index) => {
        const context = results[index]?.data;
        if (!context?.usesMembershipVariables) {
          return;
        }
        blockedFormIds.push(form_0.id);
        (context.hasLinkedPlan ? linked : unlinked).push(form_0.name);
      });
      return {
        isPending: results.some(_temp2),
        blockedFormIds,
        blockedReason: blockedReasonFor(linked, unlinked),
        checkFailed: false
      };
    };
    $[5] = enabled;
    $[6] = forms;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== t0 || $[9] !== t1) {
    t2 = {
      queries: t0,
      combine: t1
    };
    $[8] = t0;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return useQueries(t2);
}
function _temp2(result_0) {
  return result_0.isPending || result_0.isFetching;
}
function _temp(result) {
  return result.isError;
}